/**
 * Godot `Environment` glow: the bright pass, the mip pyramid and the blend, which
 * must agree for a halo to land where Godot puts it. Ported from Godot 4.6's
 * `servers/rendering/renderer_rd/shaders/effects/{copy,tonemap}.glsl`, used under
 * the MIT licence (see THIRD-PARTY-NOTICES.md). Pure, so the arithmetic is testable.
 */
import { clamp } from '../../godot/math/math.js';
import { GlowBlendMode } from '../../godot/rendering/environment/glow.js';
import { glslFloat } from '../../utils/glslLiterals';
/**
 * Godot renders the pyramid down to the coarsest level above this weight
 * (`renderer_scene_render_rd.cpp:587`), so a lighter level is never drawn.
 */
const PYRAMID_LEVEL_CUTOFF = 0.01;
/** The composite skips a level at or below this weight (`tonemap.glsl:380`). */
const GATHER_LEVEL_CUTOFF = 0.0001;
/**
 * The pixel size of one glow level, given the frame it is built from. The glow texture is half
 * the frame (`render_scene_buffers_rd.cpp:531`) and each level is one mip of it, so level 0 is
 * half the frame and each level halves again. A level never falls below 1px, since a zero-sized
 * render target is not renderable.
 */
export function glowLevelSize(width, height, level) {
    const divisor = Math.pow(2, level + 1);
    return {
        width: Math.max(1, Math.floor(width / divisor)),
        height: Math.max(1, Math.floor(height / divisor)),
    };
}
/**
 * The highest level index the glow texture has for a frame. Godot allocates it with one mip fewer
 * than the frame's halvings to 1px (`render_scene_buffers_rd.cpp:521,531`) and builds no level past
 * the last mip (`renderer_scene_render_rd.cpp:593`), so the top index is the halvings less two.
 * Level 0 stays for a frame too small to halve.
 */
export function glowLevelCap(width, height) {
    const halvingsToOnePixel = Math.floor(Math.log2(Math.max(width, height)));
    return Math.max(0, halvingsToOnePixel - 2);
}
/**
 * `null` when this environment cannot glow, so a caller decides from the settings
 * whether to mount the post-process. A pyramid with every weight at or below the
 * cutoff produces nothing, so it is `null` too: a non-null result always has a
 * real `maxLevel`.
 */
export function glowParamsFor(settings) {
    const glow = settings.glow;
    if (!glow)
        return null;
    const levels = glow.levels;
    let maxLevel = -1;
    for (let i = 0; i < levels.length; i++) {
        if ((levels[i] ?? 0) > PYRAMID_LEVEL_CUTOFF)
            maxLevel = i;
    }
    if (maxLevel < 0)
        return null;
    const gatheredLevels = [];
    for (let i = 0; i <= maxLevel; i++) {
        if ((levels[i] ?? 0) > GATHER_LEVEL_CUTOFF)
            gatheredLevels.push(i);
    }
    const blendMode = glow.blendMode;
    return {
        levels,
        maxLevel,
        gatheredLevels,
        hdrThreshold: Math.max(0, glow.hdrThreshold),
        hdrScale: Math.max(0, glow.hdrScale),
        bloom: clamp(glow.bloom, 0, 1),
        luminanceCap: Math.max(0, glow.luminanceCap),
        strength: Math.max(0, glow.strength),
        intensity: blendMode === GlowBlendMode.MIX ? clamp(glow.mix, 0, 1) : glow.intensity,
        blendMode,
        exposure: settings.toneMapping.exposure,
        map: glow.map,
        mapStrength: glow.map ? glow.mapStrength : 0,
    };
}
/** Godot skips the map at or below this strength (`tonemap.glsl:885`). */
const GLOW_MAP_EPSILON = 0.001;
/** Whether the composite scales the glow by `glow_map`. */
export function samplesGlowMap(params) {
    return params.mapStrength > GLOW_MAP_EPSILON;
}
/**
 * Whether the blend runs on tonemapped operands rather than on linear HDR. Godot
 * composites SOFTLIGHT after the curve, with the glow buffer tonemapped too, so its
 * polynomial sees the range it is anchored for. Every other mode composites before.
 */
export function blendsAfterToneMapping(params) {
    return params.blendMode === GlowBlendMode.SOFTLIGHT;
}
/**
 * Godot's bright pass (`copy.glsl:183-200`, `MODE_GLOW` under `FLAG_GLOW_FIRST_PASS`). It
 * gates on the peak RGB channel, not Rec. 709 luminance, so a saturated blue emissive
 * still blooms. It multiplies the colour by the knee rather than subtracting the
 * threshold, and `glow_bloom` floors the knee: at 1.0 every pixel glows. The colour is divided by
 * the luminance over the scale between the strength and the exposure (`copy.glsl:187-198`). The
 * caller defines `godotAutoExposureGain`.
 */
export function brightPassGlsl(params) {
    // Godot's order: strength and exposure multiply before the knee and the cap.
    // Applied afterwards, they change which pixels cross the threshold and let a
    // level exceed a cap Godot would have applied.
    const kneeEnd = params.hdrThreshold + Math.max(params.hdrScale, GLSL_EPSILON);
    return /* glsl */ `
vec3 godotGlowBrightPass(vec3 color) {
  color *= ${glslFloat(params.strength)};
  color *= godotAutoExposureGain();
  color *= ${glslFloat(params.exposure)};
  float luminance = max(color.r, max(color.g, color.b));
  float feedback = max(
    smoothstep(${glslFloat(params.hdrThreshold)}, ${glslFloat(kneeEnd)}, luminance),
    ${glslFloat(params.bloom)}
  );
  return min(color * feedback, vec3(${glslFloat(params.luminanceCap)}));
}
`;
}
/**
 * Godot's `apply_glow` (`tonemap.glsl`), specialised to one blend mode so no branch
 * survives into the shader. `white` is the tonemapper's white point, which SCREEN
 * normalises against. `godotCompositor.ts` joins a blend to a curve, so asking
 * whether an environment glows loads no tone-curve body.
 */
export function blendGlsl(params, white) {
    // A mode whose value is outside the enum falls back to ADDITIVE: `glow_blend_mode`
    // is parsed leniently, so a scene can carry anything.
    const body = BLEND_BODIES[params.blendMode] ?? BLEND_BODIES[GlowBlendMode.ADDITIVE];
    return /* glsl */ `
vec3 godotGlowBlend(vec3 color, vec3 glow) {
${body(params, white)}
}
`;
}
/**
 * Smallest value safe to bake where GLSL divides by it: `smoothstep(e, e, x)` is
 * undefined at zero width, and SCREEN divides by the white point, which a scene can
 * author as zero. Not `GATHER_LEVEL_CUTOFF`, though equal: that is Godot's weight cutoff,
 * and merging them couples a parity value to a codegen guard.
 */
const GLSL_EPSILON = 1e-4;
/**
 * The W3C soft-light `D()`: `((16c - 12)c + 4)c` below 0.25, `sqrt(c)` above,
 * applied as `color + glow * (D(color) - color)`. A channel above 1.0 is left
 * alone, where the curve would invert.
 */
const SOFTLIGHT_CHANNEL = (channel) => /* glsl */ `
  color.${channel} = color.${channel} > 1.0
    ? color.${channel}
    : color.${channel} + glow.${channel} * ((color.${channel} <= 0.25
        ? ((16.0 * color.${channel} - 12.0) * color.${channel} + 4.0) * color.${channel}
        : sqrt(color.${channel})) - color.${channel});`;
/** One body per mode, each interpolating only the constants it reads. */
const BLEND_BODIES = {
    [GlowBlendMode.ADDITIVE]: () => /* glsl */ `  return color + glow;`,
    // Screen normalised to the white range and back, in Godot's simplified form. It
    // clamps the glow to `white` because a negative light can drive the buffer below zero.
    [GlowBlendMode.SCREEN]: (_params, white) => {
        const safeWhite = glslFloat(Math.max(white, GLSL_EPSILON));
        return /* glsl */ `  glow = clamp(glow, 0.0, ${safeWhite});
  return color + glow - (color * glow / ${safeWhite});`;
    },
    [GlowBlendMode.SOFTLIGHT]: () => /* glsl */ `  glow = clamp(glow, 0.0, 1.0);
${SOFTLIGHT_CHANNEL('r')}
${SOFTLIGHT_CHANNEL('g')}
${SOFTLIGHT_CHANNEL('b')}
  return color;`,
    [GlowBlendMode.REPLACE]: () => /* glsl */ `  return glow;`,
    // `params.intensity` holds `glow_mix`, the lerp factor, and is already multiplied
    // into `glow`, so the lerp uses that same value.
    [GlowBlendMode.MIX]: (params) => 
    /* glsl */ `  return color * (1.0 - ${glslFloat(params.intensity)}) + glow;`,
};
//# sourceMappingURL=godotGlow.js.map